/**
 * sl-oee3.2.1: Seat quota display in Members tab.
 * Shows a progress bar with member usage and always provides a CTA
 * to buy/manage seats (for paid plans) or upgrade (for free).
 */

import { Button } from '@affine/component';
import {
  SeatAddonService,
  SubscriptionService,
} from '@affine/core/modules/cloud';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import { useI18n } from '@affine/i18n';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { SettingState } from '../../types';
import { SeatManagerDialog } from '../billing/seat-manager-dialog';
import { QuotaBar } from '../storage/workspace-quota';

/** Map a 0–100 percentage to a color: green → orange → red */
function quotaColor(percent: number): string {
  if (percent >= 95) return cssVarV2('status/error');
  if (percent >= 80) return cssVarV2('status/warning' as any);
  return cssVarV2('toast/iconState/regular');
}

export const SeatQuotaBanner = ({
  onChangeSettingState,
}: {
  onChangeSettingState: (settingState: SettingState) => void;
}) => {
  const t = useI18n();

  const workspaceQuotaService = useService(WorkspaceQuotaService);
  const quota = useLiveData(workspaceQuotaService.quota.quota$);

  const subscriptionService = useService(SubscriptionService);
  const plan = useLiveData(
    subscriptionService.subscription.activePlan$.map(s => s?.plan)
  );

  const seatAddonService = useService(SeatAddonService);
  const hasExtraSeats = useLiveData(seatAddonService.seatAddon.hasExtraSeats$);
  const addon = useLiveData(seatAddonService.seatAddon.addon$);
  const canPurchaseSeats = useLiveData(
    seatAddonService.seatAddon.canPurchaseSeats$
  );

  const [showSeatManager, setShowSeatManager] = useState(false);

  useEffect(() => {
    workspaceQuotaService.quota.revalidate();
    seatAddonService.seatAddon.revalidate();
  }, [workspaceQuotaService, seatAddonService]);

  const handleViewPlans = useCallback(() => {
    onChangeSettingState({
      activeTab: 'workspace:plans',
      scrollAnchor: 'cloudPricingPlan',
    });
  }, [onChangeSettingState]);

  // Compute effective limit (base + extra seats) and derived values
  const { effectiveLimit, percent, color } = useMemo(() => {
    if (!quota) return { effectiveLimit: 0, percent: null, color: null };
    const extraSeats = hasExtraSeats && addon ? addon.quantity : 0;
    const limit = quota.memberLimit + extraSeats;
    const p =
      limit > 0
        ? Math.min(100, Math.round((quota.memberCount / limit) * 100))
        : 0;
    return { effectiveLimit: limit, percent: p, color: quotaColor(p) };
  }, [quota, hasExtraSeats, addon]);

  if (!quota) {
    return null;
  }

  const used = quota.memberCount;
  const isFree = !plan;
  const extraSeats = hasExtraSeats && addon ? addon.quantity : 0;
  const isUnlimited = effectiveLimit >= Number.MAX_SAFE_INTEGER;
  const limitText = isUnlimited ? '∞' : String(effectiveLimit);
  const displayPercent = isUnlimited ? 0 : percent;

  // Free user → quota bar + upgrade nudge
  if (isFree) {
    return (
      <div style={{ marginBottom: '12px' }}>
        <QuotaBar
          label={t['Members']()}
          usedText={String(used)}
          limitText={limitText}
          percent={displayPercent}
          color={color}
        />
        <div style={{ marginTop: '8px' }}>
          <Button variant="primary" onClick={handleViewPlans}>
            {t['com.streamlain.payment.seat-quota.free.action']()}
          </Button>
        </div>
      </div>
    );
  }

  // Paid plan: QuotaBar + always-visible CTA to buy/manage seats
  return (
    <div style={{ marginBottom: '12px' }}>
      <QuotaBar
        label={t['Members']()}
        usedText={String(used)}
        limitText={limitText}
        percent={displayPercent}
        color={color}
      />
      {canPurchaseSeats && (
        <>
          <div style={{ marginTop: '8px' }}>
            <Button
              variant="secondary"
              onClick={() => setShowSeatManager(true)}
            >
              {hasExtraSeats
                ? t['com.streamlain.payment.seat-quota.with-addon.action']()
                : t['com.streamlain.payment.seat-quota.at-limit.action']()}
            </Button>
          </div>
          <SeatManagerDialog
            open={showSeatManager}
            onOpenChange={setShowSeatManager}
            currentQuantity={extraSeats}
          />
        </>
      )}
    </div>
  );
};
